import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, X, AlertTriangle, Eye, EyeOff, Mail, Send, Zap, ExternalLink, Loader2, Lock, Shield } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import ModalCloseBtn from "@/components/ModalCloseBtn";

/**
 * Provider presets — selecting a provider auto-fills host/port/security.
 * `note` shows next to the form so the admin knows exactly which password field to use.
 */
const PROVIDERS = [
  { key: "gmail-587",  label: "Gmail (STARTTLS)", host: "smtp.gmail.com", port: 587, use_ssl: false, use_starttls: true,
    note: "Username = your full Gmail. Password = 16-char App Password (NOT account password). Requires 2-Step Verification.",
    docs: "https://myaccount.google.com/apppasswords" },
  { key: "gmail-465",  label: "Gmail (SSL)",      host: "smtp.gmail.com", port: 465, use_ssl: true,  use_starttls: false,
    note: "Same Gmail App Password — just connects on port 465 with TLS-on-connect.",
    docs: "https://myaccount.google.com/apppasswords" },
  { key: "workspace",  label: "Google Workspace SMTP-Relay", host: "smtp-relay.gmail.com", port: 587, use_ssl: false, use_starttls: true,
    note: "Workspace admins: enable Apps → Gmail → Routing → SMTP relay service first, allow this server's IP.",
    docs: "https://support.google.com/a/answer/2956491" },
  { key: "outlook",    label: "Outlook / Office 365",    host: "smtp.office365.com", port: 587, use_ssl: false, use_starttls: true,
    note: "Modern Outlook requires OAuth2 or an App Password (Microsoft is deprecating basic SMTP).",
    docs: "https://account.microsoft.com/security" },
  { key: "sendgrid",   label: "SendGrid",         host: "smtp.sendgrid.net", port: 587, use_ssl: false, use_starttls: true,
    note: "Username MUST be the literal string 'apikey'. Password = your SG.xxxx API key.",
    docs: "https://app.sendgrid.com/settings/api_keys" },
  { key: "resend",     label: "Resend",           host: "smtp.resend.com",   port: 465, use_ssl: true,  use_starttls: false,
    note: "Username = 'resend'. Password = your re_xxx API key.",
    docs: "https://resend.com/api-keys" },
  { key: "mailgun",    label: "Mailgun",          host: "smtp.mailgun.org",  port: 587, use_ssl: false, use_starttls: true,
    note: "Username = postmaster@your-mailgun-domain. Password = SMTP credential from Mailgun dashboard.",
    docs: "https://app.mailgun.com/app/sending/domains" },
  { key: "zoho",       label: "Zoho Mail",        host: "smtp.zoho.com",     port: 465, use_ssl: true,  use_starttls: false,
    note: "Use a Zoho App Password from Security → App Passwords (TFA must be on).",
    docs: "https://accounts.zoho.com" },
  { key: "ses",        label: "Amazon SES",       host: "email-smtp.us-east-1.amazonaws.com", port: 587, use_ssl: false, use_starttls: true,
    note: "Use SES SMTP credentials (NOT your AWS access key). Replace region in host if needed.",
    docs: "https://console.aws.amazon.com/ses/" },
  { key: "custom",     label: "Custom",           host: "", port: 587, use_ssl: false, use_starttls: true, note: "Enter any SMTP host & port.", docs: "" },
];

export default function SmtpModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    host: initial?.host || "",
    port: initial?.port || 587,
    user: initial?.user || "",
    password: "",
    from_email: initial?.from_email || initial?.user || "",
    from_name: initial?.from_name || "MUNner's",
    use_ssl: !!initial?.use_ssl,
    use_starttls: initial?.use_starttls ?? true,
  });
  const [provider, setProvider] = useState(() => detectProvider(initial));
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyBeforeSave, setVerifyBeforeSave] = useState(true);
  const [forceSave, setForceSave] = useState(false);

  const selectProvider = (key) => {
    const p = PROVIDERS.find((x) => x.key === key) || PROVIDERS[PROVIDERS.length - 1];
    setProvider(p);
    setForm((f) => ({
      ...f,
      host: p.host || f.host,
      port: p.port,
      use_ssl: p.use_ssl,
      use_starttls: p.use_starttls,
    }));
    setVerifyResult(null);
  };

  const runVerify = async () => {
    if (!form.host || !form.user || !form.password) {
      toast.error("Fill host, user and password first");
      return;
    }
    setBusy(true); setVerifyResult(null);
    try {
      const r = await api.post("/admin/smtp/verify", form);
      setVerifyResult(r.data);
      if (r.data.ok) toast.success("Credentials accepted ✓");
      else toast.error("Server rejected credentials — see steps below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally { setBusy(false); }
  };

  const save = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const payload = { ...form, verify_before_save: verifyBeforeSave && !forceSave };
      const r = await api.post("/admin/smtp", payload);
      if (r.data.ok) {
        toast.success(r.data.verified ? "Saved & verified" : "Saved (verification skipped)");
        onSaved?.();
        onClose?.();
      } else {
        setVerifyResult(r.data.diagnose);
        toast.error(r.data.message || "Save blocked by verification failure");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/smtp/test", {});
      if (r.data.sent) toast.success("Test email sent ✓");
      else toast.error(r.data.message || "Send failed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3 sm:p-6" data-testid="smtp-modal">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col relative">
        <ModalCloseBtn onClose={onClose} />

        <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl brand-gradient text-white flex items-center justify-center shadow-md"><Mail className="w-6 h-6" /></div>
          <div className="flex-1">
            <h2 className="font-display font-black text-2xl text-slate-900">SMTP Email Setup</h2>
            <p className="text-xs text-slate-500 mt-0.5">Pick a provider, paste your credentials, click <b>Test connection</b>. Nothing is saved until the test passes (unless you force-save).</p>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <form onSubmit={save} className="px-6 py-5 space-y-5">
            {/* Provider chooser */}
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Provider</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="smtp-provider-grid">
                {PROVIDERS.map((p) => (
                  <button
                    type="button"
                    key={p.key}
                    onClick={() => selectProvider(p.key)}
                    className={`px-3 py-2 rounded-xl text-sm font-semibold border-2 transition-all text-left ${
                      provider.key === p.key
                        ? "border-orange-500 bg-orange-50 text-orange-900"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                    }`}
                    data-testid={`smtp-provider-${p.key}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              {provider.note && (
                <div className="mt-2 rounded-xl bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 leading-relaxed">
                  <div className="font-bold mb-0.5">ℹ {provider.label} — what to enter</div>
                  {provider.note}
                  {provider.docs && (
                    <a href={provider.docs} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-blue-700 hover:underline font-semibold">
                      Get credentials <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Connection details */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold text-slate-700 mb-1">SMTP host</label>
                <input className="input-pill" placeholder="smtp.example.com" value={form.host}
                  onChange={(e) => setForm({ ...form, host: e.target.value })} data-testid="smtp-host" required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Port</label>
                <input type="number" className="input-pill" value={form.port}
                  onChange={(e) => setForm({ ...form, port: parseInt(e.target.value) || 587 })} data-testid="smtp-port" required />
              </div>
            </div>

            {/* Security */}
            <div className="grid grid-cols-2 gap-2">
              <label className={`px-3 py-2.5 rounded-xl border-2 flex items-center gap-2 cursor-pointer text-sm ${form.use_ssl ? "border-emerald-400 bg-emerald-50 text-emerald-900" : "border-slate-200"}`}>
                <input type="checkbox" checked={form.use_ssl}
                  onChange={(e) => setForm({ ...form, use_ssl: e.target.checked, use_starttls: e.target.checked ? false : form.use_starttls })}
                  className="hidden" />
                <Lock className="w-4 h-4" /> <span><b>SSL/TLS on connect</b> (port 465)</span>
              </label>
              <label className={`px-3 py-2.5 rounded-xl border-2 flex items-center gap-2 cursor-pointer text-sm ${form.use_starttls ? "border-emerald-400 bg-emerald-50 text-emerald-900" : "border-slate-200"}`}>
                <input type="checkbox" checked={form.use_starttls}
                  onChange={(e) => setForm({ ...form, use_starttls: e.target.checked, use_ssl: e.target.checked ? false : form.use_ssl })}
                  className="hidden" />
                <Shield className="w-4 h-4" /> <span><b>STARTTLS</b> (port 587)</span>
              </label>
            </div>

            {/* Auth */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">SMTP username</label>
                <input className="input-pill" placeholder="you@example.com" value={form.user}
                  onChange={(e) => setForm({ ...form, user: e.target.value })} data-testid="smtp-user" required />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Password / App password
                  {initial?.has_password && !form.password && <span className="ml-1 text-emerald-600 font-bold">(saved: {initial.password_length} chars)</span>}
                </label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    className="input-pill pr-10"
                    placeholder={initial?.has_password ? "•••••••• (leave blank to keep)" : "Paste password / app password"}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="smtp-password"
                  />
                  <button type="button" tabIndex={-1}
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-700">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {form.password && (
                  <div className="text-[10px] text-slate-500 mt-1">
                    Length after stripping spaces: <b>{form.password.replace(/\s+/g, "").length}</b> chars
                    {form.host.toLowerCase().includes("gmail") && (
                      <span> {form.password.replace(/\s+/g, "").length === 16 ? "✓ matches Gmail App Password length" : "⚠ Gmail App Passwords are exactly 16 chars"}</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* From identity */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">&ldquo;From&rdquo; email</label>
                <input className="input-pill" placeholder="noreply@yourdomain.com" value={form.from_email}
                  onChange={(e) => setForm({ ...form, from_email: e.target.value })} data-testid="smtp-from-email" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">&ldquo;From&rdquo; name</label>
                <input className="input-pill" placeholder="MUNner's" value={form.from_name}
                  onChange={(e) => setForm({ ...form, from_name: e.target.value })} data-testid="smtp-from-name" />
              </div>
            </div>

            {/* Verification result panel */}
            {verifyResult && (
              <div className={`rounded-2xl p-4 border-2 ${verifyResult.ok ? "border-emerald-200 bg-emerald-50" : "border-rose-200 bg-rose-50"}`} data-testid="smtp-verify-result">
                <div className={`flex items-center gap-2 font-display font-bold mb-2 ${verifyResult.ok ? "text-emerald-900" : "text-rose-900"}`}>
                  {verifyResult.ok ? <Check className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                  {verifyResult.ok ? "Credentials accepted" : "Credentials rejected by SMTP server"}
                </div>
                <div className="space-y-1.5">
                  {(verifyResult.steps || []).map((st, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <span className={`mt-0.5 w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${st.ok ? "bg-emerald-500" : "bg-rose-500"} text-white`}>
                        {st.ok ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                      </span>
                      <div className="flex-1">
                        <b className={st.ok ? "text-emerald-800" : "text-rose-800"}>{st.step}</b>
                        <div className={`leading-relaxed ${st.ok ? "text-emerald-700" : "text-rose-800"}`}>{st.message}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {!verifyResult.ok && (
                  <label className="mt-3 flex items-center gap-2 text-xs text-rose-900 cursor-pointer" data-testid="smtp-force-save-label">
                    <input type="checkbox" checked={forceSave} onChange={(e) => setForceSave(e.target.checked)} data-testid="smtp-force-save" />
                    Save anyway (ignore verification failure) — emails will fall back to dev_otp on the login page
                  </label>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-2 pt-2">
              <button type="button" onClick={runVerify} disabled={busy}
                className="btn-ghost py-3 px-4 flex-1 flex items-center justify-center gap-2"
                data-testid="smtp-verify-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                Test connection
              </button>
              <button type="button" onClick={sendTest} disabled={busy || !initial?.configured}
                className="btn-ghost py-3 px-4 flex-1 flex items-center justify-center gap-2"
                data-testid="smtp-send-test-btn" title={!initial?.configured ? "Save config first" : ""}>
                <Send className="w-4 h-4" /> Send me a test email
              </button>
              <button type="submit" disabled={busy}
                className="btn-primary py-3 px-6 flex-1 flex items-center justify-center gap-2"
                data-testid="smtp-save">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
              </button>
            </div>

            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer pb-2">
              <input type="checkbox" checked={verifyBeforeSave} onChange={(e) => setVerifyBeforeSave(e.target.checked)} data-testid="smtp-verify-before-save" />
              Verify credentials before saving (recommended — blocks bad passwords from being persisted)
            </label>
          </form>
        </div>
      </motion.div>
    </div>
  );
}

function detectProvider(initial) {
  if (!initial?.host) return PROVIDERS[0];
  const h = initial.host.toLowerCase();
  if (h.includes("smtp-relay.gmail")) return PROVIDERS.find(p => p.key === "workspace");
  if (h.includes("gmail.com")) return initial.use_ssl ? PROVIDERS.find(p => p.key === "gmail-465") : PROVIDERS.find(p => p.key === "gmail-587");
  if (h.includes("office365") || h.includes("outlook")) return PROVIDERS.find(p => p.key === "outlook");
  if (h.includes("sendgrid")) return PROVIDERS.find(p => p.key === "sendgrid");
  if (h.includes("resend")) return PROVIDERS.find(p => p.key === "resend");
  if (h.includes("mailgun")) return PROVIDERS.find(p => p.key === "mailgun");
  if (h.includes("zoho")) return PROVIDERS.find(p => p.key === "zoho");
  if (h.includes("amazonaws")) return PROVIDERS.find(p => p.key === "ses");
  return PROVIDERS.find(p => p.key === "custom");
}
